import React, { useEffect, useState } from "react";
import { Building2, CheckCircle2, KeyRound, Radio, RefreshCw, Trash2, Video } from "lucide-react";
import apiClient from "../../utils/apiClient";
import Shimmer from "../Common/Shimmer";
import CommonToaster from "../Common/CommonToaster";
import {
  AppButton,
  AppCard,
  AppCardContent,
  AppCardHeader,
  AppCardTitle,
  Callout,
  ConfirmModal,
  FormField,
  Input,
  PanelButton,
  StatusBadge,
  cn,
} from "../ui";

/** The picked panel. Not a PanelButton prop — the component owns its geometry
 *  and its hover/focus states, and selection is the call site's meaning. */
const SELECTED_PANEL = "tw-border-primary tw-bg-primary-soft";

/**
 * GratsMeetSettings — the institute's view of our own meeting SDK.
 *
 * Deliberately short. In the normal case there is nothing to connect: classes
 * run on the platform video organization the SaaS owns, so this screen is a
 * status readout. The credential half only appears for an institute that has
 * been given its own video organization, and even then the key is write-only —
 * the server returns a four-character hint and never the key itself.
 *
 * Tailwind only, no stylesheet: this module is new, and a half-converted file
 * is a specificity coin-flip decided by bundler order (TOKEN_CONTRACT §6.1).
 */
const GratsMeetSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const showToast = (message, type = "success") => setToast({ show: true, message, type });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/institute/grats-meet-settings");
      setSettings(data);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to load Grats Meet settings.", "danger");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const save = async (body, successMessage) => {
    setSaving(true);
    try {
      await apiClient.put("/institute/grats-meet-settings", body);
      showToast(successMessage);
      setApiKey("");
      await load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Could not save.", "danger");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await apiClient.post("/institute/grats-meet-settings/test");
      showToast(data.message || "Connected to Grats Meet.");
    } catch (err) {
      showToast(err?.response?.data?.message || "Connection test failed.", "danger");
    } finally {
      setTesting(false);
    }
  };

  const disconnect = async () => {
    setConfirmDisconnect(false);
    setSaving(true);
    try {
      const { data } = await apiClient.delete("/institute/grats-meet-settings/key");
      showToast(data.message || "Disconnected.");
      await load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Could not disconnect.", "danger");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        <Shimmer height={120} />
        <Shimmer height={200} />
      </div>
    );
  }

  if (!settings) return null;

  const isSelfMode = settings.mode === "self";
  const isActiveProvider = settings.meetingProvider === "grats";

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {!settings.serverEnabled ? (
        <Callout tone="info">
          Grats Meet is not switched on for this platform yet. Contact support to have it enabled
          for your institute.
        </Callout>
      ) : null}

      {/* A free trial has Grats Meet whatever its plan says (`trial` is set). */}
      {settings.serverEnabled && !settings.allowedByPlan && !settings.trial ? (
        <Callout tone="warning">
          Your{settings.planName ? ` ${settings.planName}` : ""} plan does not include Grats Meet
          live classes. Upgrade or contact support to switch your classes over.
        </Callout>
      ) : null}

      {/* ── Status ─────────────────────────────────────────────────────── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <span className="tw-inline-flex tw-items-center tw-gap-2">
              <Video size={18} aria-hidden="true" /> Grats Meet
            </span>
          </AppCardTitle>
          <p className="tw-m-0 tw-text-sm tw-text-content-muted">
            Our own live-class platform. Classes run inside the LMS — students and trainers join
            from their own dashboard, and there is no meeting link to share or leak.
          </p>
        </AppCardHeader>
        <AppCardContent>
          <dl className="tw-m-0 tw-grid tw-gap-4 sm:tw-grid-cols-3">
            <div>
              <dt className="tw-text-xs tw-uppercase tw-tracking-caps tw-text-content-subtle">Status</dt>
              <dd className="tw-mt-1 tw-mb-0">
                {isActiveProvider ? (
                  <StatusBadge status="active" dot>Live classes use Grats Meet</StatusBadge>
                ) : (
                  <StatusBadge status="inactive">Not the active provider</StatusBadge>
                )}
              </dd>
            </div>
            <div>
              <dt className="tw-text-xs tw-uppercase tw-tracking-caps tw-text-content-subtle">
                Concurrent classes
              </dt>
              <dd className="tw-mt-1 tw-mb-0 tw-text-sm tw-text-content">
                {settings.maxConcurrentMeetings > 0
                  ? `${settings.liveNow} of ${settings.maxConcurrentMeetings} in use`
                  : "Not included in your plan"}
              </dd>
            </div>
            <div>
              <dt className="tw-text-xs tw-uppercase tw-tracking-caps tw-text-content-subtle">Account</dt>
              <dd className="tw-mt-1 tw-mb-0 tw-text-sm tw-text-content">
                {isSelfMode ? "Your own Grats Meet account" : "Platform account (managed for you)"}
              </dd>
            </div>
          </dl>

          <div className="tw-mt-4 tw-flex tw-flex-wrap tw-gap-2">
            <AppButton variant="outline" size="sm" onClick={test} disabled={testing || !settings.serverEnabled}>
              <RefreshCw size={15} className={cn(testing && "tw-animate-spin")} aria-hidden="true" />
              {testing ? "Testing…" : "Test connection"}
            </AppButton>
          </div>
        </AppCardContent>
      </AppCard>

      {/* ── Which account hosts the classes ─────────────────────────────── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <span className="tw-inline-flex tw-items-center tw-gap-2">
              <Building2 size={18} aria-hidden="true" /> Hosting account
            </span>
          </AppCardTitle>
          <p className="tw-m-0 tw-text-sm tw-text-content-muted">
            Most institutes need nothing here. Bring your own account only if your recordings and
            usage have to be billed and stored separately from the platform.
          </p>
        </AppCardHeader>
        <AppCardContent>
          <div className="tw-grid tw-gap-3 md:tw-grid-cols-2">
            <PanelButton
              aria-pressed={!isSelfMode}
              className={cn(!isSelfMode && SELECTED_PANEL)}
              disabled={saving}
              onClick={() => (isSelfMode ? save({ mode: "platform" }, "Now using the platform account.") : undefined)}
            >
              <span className="tw-flex tw-items-start tw-gap-2">
                <Radio size={16} className="tw-mt-0.5 tw-shrink-0" aria-hidden="true" />
                <span>
                  <span className="tw-block tw-font-semibold">Platform account</span>
                  <span className="tw-block tw-text-xs tw-text-content-muted">
                    Nothing to set up. Capacity and recording storage come with your plan.
                  </span>
                </span>
              </span>
            </PanelButton>

            <PanelButton
              aria-pressed={isSelfMode}
              className={cn(isSelfMode && SELECTED_PANEL)}
              disabled={saving || !settings.connected}
              onClick={() =>
                (isSelfMode ? undefined : save({ mode: "self" }, "Now using your own Grats Meet account."))
              }
            >
              <span className="tw-flex tw-items-start tw-gap-2">
                <KeyRound size={16} className="tw-mt-0.5 tw-shrink-0" aria-hidden="true" />
                <span>
                  <span className="tw-block tw-font-semibold">Your own account</span>
                  <span className="tw-block tw-text-xs tw-text-content-muted">
                    {settings.connected
                      ? `Key saved (${settings.apiKeyHint || "••••"}).`
                      : "Add an API key below to enable."}
                  </span>
                </span>
              </span>
            </PanelButton>
          </div>

          <div className="tw-mt-4 tw-max-w-xl">
            <FormField
              label="Grats Meet API key"
              hint={
                settings.connected
                  ? "A saved key is never shown again. Paste a new one to replace it."
                  : "Starts with vsk_live_ or vsk_test_. It is verified against the platform before it is stored, and encrypted at rest."
              }
            >
              <Input
                type="password"
                value={apiKey}
                autoComplete="off"
                placeholder="vsk_live_…"
                onChange={(e) => setApiKey(e.target.value)}
              />
            </FormField>

            <div className="tw-mt-3 tw-flex tw-flex-wrap tw-gap-2">
              <AppButton
                size="sm"
                disabled={saving || !apiKey.trim()}
                onClick={() => save({ apiKey: apiKey.trim() }, "API key verified and saved.")}
              >
                <CheckCircle2 size={15} aria-hidden="true" />
                {saving ? "Saving…" : "Save key"}
              </AppButton>
              {settings.connected ? (
                <AppButton
                  variant="outline-destructive"
                  size="sm"
                  disabled={saving}
                  onClick={() => setConfirmDisconnect(true)}
                >
                  <Trash2 size={15} aria-hidden="true" />
                  Disconnect
                </AppButton>
              ) : null}
            </div>
          </div>
        </AppCardContent>
      </AppCard>

      {/* Recording works differently from Zoom's, and a trainer finding that
          out after a class is how a class gets lost — so it is said here.
          ⚠️ Do not promise a timing: the video platform assembles the file
          after the class ends, and how long that takes is not ours to state. */}
      <Callout tone="info">
        <span>
          <strong>Class recordings.</strong> A Grats Meet class starts recording on its own and
          the finished video appears on the batch's <strong>Videos</strong> tab, the same place
          Zoom recordings land. It is not there the moment the class ends — the video platform
          has to assemble the file first. If one has not appeared, press <strong>Sync</strong> on
          the Videos tab; if it still does not, contact support.
        </span>
      </Callout>

      {/* Only the shared screen is captured. Not a caveat we can fix from here,
          and the one that silently produces an empty recording. */}
      <Callout tone="warning">
        <span>
          <strong>Recording captures the shared screen.</strong> A class taught to camera with
          nothing shared records no picture. Ask trainers to share their screen, slides or
          whiteboard for the parts that need to be kept.
        </span>
      </Callout>

      <ConfirmModal
        show={confirmDisconnect}
        title="Disconnect your Grats Meet account?"
        message="Live classes will fall back to the platform account. Recordings already stored in the LMS are unaffected."
        confirmText="Disconnect"
        variant="danger"
        onConfirm={disconnect}
        onHide={() => setConfirmDisconnect(false)}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default GratsMeetSettings;
